import React from 'react';
import { css } from '@emotion/react';
import theme from '@/styles/theme';
import useTranslation from 'next-translate/useTranslation';

export interface IStoryGameOnboardingAlert {
  title: string;
  subtitle?: string;
  cancelText?: string;
  confirmText?: string;
  onCancel?: () => void;
  onConfirm?: () => void;
  isVisible?: boolean;
  backgroundColor?: string;
  RenderComponent?: React.FC;
}

const StoryGameOnboardingAlert = (props: {
  storygameAlertData: IStoryGameOnboardingAlert;
  buttonType?: 'horizontal' | 'vertical';
}) => {
  const { buttonType = 'horizontal', storygameAlertData } = props;
  const {
    title,
    subtitle,
    cancelText,
    confirmText,
    onCancel,
    onConfirm,
    isVisible,
    backgroundColor = '#000000bf',
    RenderComponent,
  } = storygameAlertData;
  const { t } = useTranslation();
  if (!isVisible) return null;
  return (
    <div css={containerStyle}>
      <div css={cardStyle} style={{ backgroundColor }}>
        <h1 css={titleStyle}>{title}</h1>
        <p css={subtitleStyle}>{subtitle}</p>
        <div>{RenderComponent && <RenderComponent />}</div>
        {buttonType === 'horizontal' ? (
          <div css={buttonContainerStyle}>
            {onCancel && (
              <button css={cancelButtonStyle} onClick={onCancel}>
                {cancelText ?? t('common_popup_button_cancel')}
              </button>
            )}
            {onConfirm && (
              <button css={startButtonStyle} onClick={onConfirm}>
                {confirmText ?? t('common_screen_title_confirm')}
              </button>
            )}
          </div>
        ) : (
          <div css={buttonVerticalContainerStyle}>
            {onConfirm && (
              <button css={startButtonStyle} onClick={onConfirm}>
                {confirmText ?? t('common_screen_title_confirm')}
              </button>
            )}
            {onCancel && (
              <button css={cancelButtonStyle} onClick={onCancel}>
                {cancelText ?? t('common_popup_button_cancel')}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const containerStyle = css`
  top: 0px;
  left: 0px;
  width: 100vw;
  height: 100%;
  background-color: #00000040;
  backdrop-filter: blur(8px);
  position: fixed;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 4;

  @media (max-width: ${theme.devices.md}px) {
    padding: 0 16px;
  }
`;

const cardStyle = css`
  background-color: #000000bf;
  border-radius: 12px;
  padding: 32px 24px 24px 24px;
  text-align: center;
  width: 400px;
`;

const titleStyle = css`
  color: var(--whiteA90, rgba(255, 255, 255, 0.9));
  text-align: center;

  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: 26px; /* 144.444% */
  color: white;
  margin-bottom: 8px;
`;

const subtitleStyle = css`
  color: #888888;
  font-size: 16px;
`;

const buttonContainerStyle = css`
  margin-top: 24px;
  display: flex;
  justify-content: center;
  gap: 6px;
`;

const buttonVerticalContainerStyle = css`
  margin-top: 24px;
  display: flex;
  justify-content: center;
  gap: 6px;
  flex-direction: column;
`;

const buttonBaseStyle = css`
  display: flex;
  flex: 1;
  justify-content: center;
  align-items: center;
  padding: 12px 0;

  border-radius: var(--radius-sm, 6px);
  border: 1px solid var(--whiteA25, rgba(255, 255, 255, 0.25));

  cursor: pointer;

  text-align: center;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px; /* 150% */
`;

const cancelButtonStyle = css`
  ${buttonBaseStyle}
  background-color: transparent;
  color: white;
`;

const startButtonStyle = css`
  ${buttonBaseStyle}
  background-color: #753BBD;
  color: white;
  border: none;
`;

export default StoryGameOnboardingAlert;
